import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import utc from 'dayjs/plugin/utc';
import {
    Ban,
    CheckCircle2,
    CreditCard,
    Download,
    Eye,
    Play,
    RotateCcw,
    Search,
    Send,
    Trash2,
    Wrench,
    X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useNavigationType } from 'react-router-dom';

import { httpClient } from '../../common/httpclient';
import { getEnterpriseIdFromToken } from '../../component/context/EnterpriseConfigContext';
import { standaloneFieldSx } from '../../common/fieldStyles';
import { messageFromApiErrorBody } from '../../common/apiErrorMessage';
import FilterToolbar, { FilterChips } from '../../component/filter/FilterToolbar';
import DialogForm from '../../component/dialog/DialogForm';
import OrderWhatsappDialog from '../../component/dialog/OrderWhatsappDialog';
import { useWhatsappConnected } from '../../component/dialog/QuoteWhatsappDialog';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import StatusChip from '../../component/common/StatusChip';
import NewItemButton from '../../component/common/NewItemButton';
import DesktopPeriodSelector from '../../component/common/DesktopPeriodSelector';
import PaymentModal from './paymentmodal/PaymentModal';
import { downloadServiceOrderPDF } from './Printer';
import { usePermissions } from '../../hooks/usePermissions';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import MobileOrdersList from '../../component/serviceorder/MobileOrdersList';
import { useEnterpriseConfigContext } from '../../component/context/EnterpriseConfigContext';

dayjs.extend(utc);

// Mesmos 5 status reais de ordem de serviço usados por `StatusChip
// domain="orderService"` (DESIGN-SYSTEM.md §4) — a API retorna "Paga", mas o
// rótulo exibido (aqui e no chip removível) é sempre "Faturada".
const STATUS_OPTIONS = ['Não Iniciada', 'Em Execução', 'Finalizada', 'Paga', 'Cancelada'];

function orderServiceStatusLabel(value) {
    return value === 'Paga' ? 'Faturada' : value;
}

const PAGE_SIZE_OPTIONS = [5, 8, 15, 25, 50];

// Snapshot dos filtros aplicados, gravado em sessionStorage (chaveado por
// empresa) SOMENTE ao abrir o detalhe de uma ordem e consumido (removido) na
// montagem seguinte da lista. Restaurado apenas se a volta for um POP.
// Também removido no logout (AccountMenu). Mesmo padrão de `sales/List.js`.
export const ORDERS_FILTERS_STORAGE_PREFIX = 'service_orders_list_filters:';

function ordersFiltersKey() {
    return `${ORDERS_FILTERS_STORAGE_PREFIX}${getEnterpriseIdFromToken() || 'anon'}`;
}

function loadSavedOrdersFilters(allowRestore) {
    const empty = { start: null, end: null, status: '', customer: '', search: '', page: 0, pageSize: 8 };
    if (!allowRestore) return empty;
    try {
        const raw = sessionStorage.getItem(ordersFiltersKey());
        if (!raw) return empty;
        const saved = JSON.parse(raw);
        if (!saved || typeof saved !== 'object') return empty;
        const parseDate = (v) => {
            if (typeof v !== 'string') return null;
            const d = dayjs(v, 'YYYY-MM-DD', true);
            return d.isValid() ? d : null;
        };
        return {
            start: parseDate(saved.start),
            end: parseDate(saved.end),
            status: STATUS_OPTIONS.includes(saved.status) ? saved.status : '',
            customer: typeof saved.customer === 'string' ? saved.customer : '',
            search: typeof saved.search === 'string' ? saved.search : '',
            page: Number.isInteger(saved.page) && saved.page >= 0 ? saved.page : 0,
            pageSize: PAGE_SIZE_OPTIONS.includes(saved.pageSize) ? saved.pageSize : 8,
        };
    } catch (error) {
        return empty;
    }
}

// Filtro de data (período aplicado) persistente em localStorage, chaveado por
// empresa. Sobrevive a navegação, F5 e novo login; só é removido ao clicar em
// "Limpar". Mesmo padrão de `sales/List.js`.
const ORDERS_DATE_FILTER_STORAGE_PREFIX = 'service_orders_list_date_filter:';

function ordersDateFilterKey() {
    return `${ORDERS_DATE_FILTER_STORAGE_PREFIX}${getEnterpriseIdFromToken() || 'anon'}`;
}

function loadPersistedOrdersDateFilter() {
    try {
        const raw = localStorage.getItem(ordersDateFilterKey());
        if (!raw) return null;
        const saved = JSON.parse(raw);
        if (!saved || typeof saved !== 'object') return null;
        const parseDate = (v) => {
            if (typeof v !== 'string') return null;
            const d = dayjs(v, 'YYYY-MM-DD', true);
            return d.isValid() ? d : null;
        };
        const start = parseDate(saved.start);
        const end = parseDate(saved.end);
        if (!start && !end) return null;
        return { start, end };
    } catch (error) {
        return null;
    }
}

function persistOrdersDateFilter(start, end) {
    try {
        const hasStart = start && start.isValid();
        const hasEnd = end && end.isValid();
        if (!hasStart && !hasEnd) {
            localStorage.removeItem(ordersDateFilterKey());
            return;
        }
        localStorage.setItem(ordersDateFilterKey(), JSON.stringify({
            start: hasStart ? start.format('YYYY-MM-DD') : null,
            end: hasEnd ? end.format('YYYY-MM-DD') : null,
        }));
    } catch (error) {
        // localStorage indisponível: apenas não persiste o filtro de data.
    }
}

export default function List() {
    const theme = useTheme();
    const { hasPermission } = usePermissions();
    // Lido uma única vez, antes da primeira requisição. Só restaura quando a
    // entrada na lista é um retorno (POP: voltar do detalhe da ordem); menu/
    // links (PUSH) e F5 abrem vazia. Mesmo padrão de `sales/List.js`.
    const navigationType = useNavigationType();
    const [initialFilters] = useState(() => {
        const filters = loadSavedOrdersFilters(navigationType === 'POP');
        // O filtro de data persistido prevalece, em qualquer tipo de navegação.
        const persistedDates = loadPersistedOrdersDateFilter();
        return persistedDates ? { ...filters, start: persistedDates.start, end: persistedDates.end } : filters;
    });
    const [search, setSearch] = useState(initialFilters.search);
    // Busca (número ou cliente) com debounce — evita uma requisição por tecla.
    const [debouncedSearch, setDebouncedSearch] = useState(initialFilters.search.trim());
    const previousDebouncedSearch = useRef(initialFilters.search.trim());
    const [loading, setLoading] = useState(true);
    // Filtro de data inicia vazio: sem datas, traz todas as ordens (mais
    // recentes primeiro), paginadas no servidor.
    const [selectedDate, setSelectedDate] = useState(initialFilters.start);
    const [selectedDateEnd, setSelectedDateEnd] = useState(initialFilters.end);
    // Período efetivamente aplicado na última busca — só muda quando o usuário
    // clica em "Buscar"/"Limpar". Alimenta o chip "Período".
    const [appliedPeriod, setAppliedPeriod] = useState({ start: initialFilters.start, end: initialFilters.end });
    const [orders, setOrders] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState(initialFilters.status);
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(initialFilters.customer);
    // Paginação server-side (page é 0-based na tela; a API usa 1-based).
    const [page, setPage] = useState(initialFilters.page);
    const [pageSize, setPageSize] = useState(initialFilters.pageSize);
    const [totalCount, setTotalCount] = useState(0);
    const [reloadKey, setReloadKey] = useState(0);
    const navigate = useNavigate();
    const [openExecuteOrderDialog, setOpenExecuteOrderDialog] = useState(false);
    const [openFinishOrderDialog, setOpenFinishOrderDialog] = useState(false);
    const [openReopenOrderDialog, setOpenReopenOrderDialog] = useState(false);
    const [openCancelOrderDialog, setOpenCancelOrderDialog] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelReasonError, setCancelReasonError] = useState('');
    const [openDeleteOrderDialog, setOpenDeleteOrderDialog] = useState(false);
    const [openErrorOrderDialog, setOpenErrorOrderDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [orderServiceId, setOrderServiceId] = useState('');
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [openPaymentModal, setOpenPaymentModal] = useState(false);
    const [selectedOrderForPayment, setSelectedOrderForPayment] = useState(null);
    const [severity, setSeverity] = useState('success');
    const [printingOrderId, setPrintingOrderId] = useState(null);
    const [orderToWhatsapp, setOrderToWhatsapp] = useState(null);
    const canSendWhatsapp = hasPermission('serviceorder.send');
    const whatsappConnected = useWhatsappConnected(canSendWhatsapp);
    const isMobileShell = useIsMobileShell();
    const { settings: enterpriseSettings } = useEnterpriseConfigContext();
    // Mobile: lista contínua ("Carregar mais") em vez de paginação; página própria para não
    // interferir na paginação do desktop/tablet.
    const MOBILE_PAGE_SIZE = 20;
    const [mobilePage, setMobilePage] = useState(0);

    // Debounce da busca; ao mudar o termo volta para a primeira página.
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
        return () => clearTimeout(timer);
    }, [search]);

    // Só volta para a primeira página se o termo realmente mudou (na montagem
    // o termo restaurado não deve zerar a página restaurada).
    useEffect(() => {
        if (previousDebouncedSearch.current === debouncedSearch) return;
        previousDebouncedSearch.current = debouncedSearch;
        setPage(0);
        setMobilePage(0);
    }, [debouncedSearch]);

    // Consome o snapshot após a montagem (restaurado ou não). Fica no efeito,
    // e não no inicializador do state, para o StrictMode não perdê-lo.
    useEffect(() => {
        try {
            sessionStorage.removeItem(ordersFiltersKey());
        } catch (error) {
            // sessionStorage indisponível: nada a limpar.
        }
    }, []);

    // Salva os filtros aplicados imediatamente antes de ir ao detalhe da ordem.
    const saveFiltersAndOpenOrder = (orderId) => {
        try {
            const hasStart = appliedPeriod.start && appliedPeriod.start.isValid();
            const hasEnd = appliedPeriod.end && appliedPeriod.end.isValid();
            sessionStorage.setItem(ordersFiltersKey(), JSON.stringify({
                start: hasStart ? appliedPeriod.start.format('YYYY-MM-DD') : null,
                end: hasEnd ? appliedPeriod.end.format('YYYY-MM-DD') : null,
                status: selectedStatus,
                customer: selectedCustomer,
                search: debouncedSearch,
                page,
                pageSize,
            }));
        } catch (error) {
            // sessionStorage indisponível: apenas não preserva os filtros.
        }
        navigate(`/serviceorders/${orderId}/view`);
    };

    // Busca a página atual no servidor sempre que filtros/página/tamanho mudam.
    useEffect(() => {
        let cancelled = false;
        const requestPage = isMobileShell ? mobilePage : page;
        const params = { page: requestPage + 1, page_size: isMobileShell ? MOBILE_PAGE_SIZE : pageSize };
        if (appliedPeriod.start && appliedPeriod.start.isValid()) params.startDate = appliedPeriod.start.format('DD/MM/YYYY');
        if (appliedPeriod.end && appliedPeriod.end.isValid()) params.endDate = appliedPeriod.end.format('DD/MM/YYYY');
        if (selectedStatus) params.status = selectedStatus;
        if (selectedCustomer) params.customerId = selectedCustomer;
        if (debouncedSearch) params.search = debouncedSearch;

        setLoading(true);
        httpClient.get('/orderservices', { params })
            .then((response) => {
                if (cancelled) return;
                const received = response.data.orders || [];
                if (isMobileShell && requestPage > 0) {
                    setOrders((prev) => [...prev, ...received.filter((row) => !prev.some((p) => p.order_service_id === row.order_service_id))]);
                } else {
                    setOrders(received);
                }
                setTotalCount(response.data.total || 0);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error('Erro ao buscar ordens de serviço:', error);
                setOrders([]);
                setTotalCount(0);
                setMessage('Erro ao buscar ordens de serviço');
                setSeverity('error');
                setOpenSnackbar(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [page, pageSize, appliedPeriod, selectedStatus, selectedCustomer, debouncedSearch, reloadKey, isMobileShell, mobilePage]);

    // Recarrega a página atual (status/contagem mudam após executar, cancelar, excluir…).
    const reloadCurrentPage = () => setReloadKey((k) => k + 1);

    const handleApplyFilters = () => {
        setAppliedPeriod({ start: selectedDate, end: selectedDateEnd });
        persistOrdersDateFilter(selectedDate, selectedDateEnd);
        setPage(0);
        setMobilePage(0);
    };

    // Mobile: a folha "Filtros" aplica cliente + período de uma vez.
    const handleApplyMobileFilters = ({ customer, start, end }) => {
        setSelectedCustomer(customer);
        setSelectedDate(start);
        setSelectedDateEnd(end);
        setAppliedPeriod({ start, end });
        persistOrdersDateFilter(start, end);
        setPage(0);
        setMobilePage(0);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPage(0);
    };

    const getCustomers = async () => {
        try {
            const response = await httpClient.get('/customers');
            setCustomers(response.data);
        } catch (error) {
            console.error('Erro ao buscar clientes:', error);
        }
    }

    const handleChangeStatus = (status) => {
        setSelectedStatus(status);
        setPage(0);
        setMobilePage(0);
    };

    const handleChangeCustomer = (customerId) => {
        setSelectedCustomer(customerId);
        setPage(0);
        setMobilePage(0);
    };

    const handleTableSearch = (searchValue) => {
        setSearch(searchValue);
    };

    // Zera tudo, inclusive as datas (voltam a "todas as datas").
    const handleClearFilters = () => {
        setSelectedDate(null);
        setSelectedDateEnd(null);
        setSelectedStatus('');
        setSelectedCustomer('');
        setSearch('');
        setDebouncedSearch('');
        setAppliedPeriod({ start: null, end: null });
        persistOrdersDateFilter(null, null);
        setPage(0);
        setMobilePage(0);
    };

    // Remove só o período (chip): limpa campo e filtro aplicado, e persiste.
    const handleClearPeriod = () => {
        setSelectedDate(null);
        setSelectedDateEnd(null);
        setAppliedPeriod({ start: null, end: null });
        persistOrdersDateFilter(null, null);
        setPage(0);
        setMobilePage(0);
    };

    const hasActiveFilters = Boolean(selectedStatus || selectedCustomer || search || selectedDate || selectedDateEnd);

    const handleExecuteOrder = (id) => {
        setOpenExecuteOrderDialog(true);
        setOrderServiceId(id);
    }


    useEffect(() => {
        getCustomers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const columns = [
        {
            path: 'number',
            name: 'Número',
            invisible: false,
            render: (row) => (
                <Typography component="span" sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                    {row.number}
                </Typography>
            ),
        },
        {
            path: 'order_date',
            name: 'Data da OS',
            invisible: false,
            render: (row) => {
                const d = new Date(row.order_date);
                if (!row.order_date || Number.isNaN(d.getTime()) || d.getUTCFullYear() < 1900) {
                    return '--';
                }
                // Mesmo formato/fuso da coluna "Data de Entrega".
                return d.toLocaleString('pt-BR', {
                    timeZone: 'UTC',
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            },
        },
        {
            path: 'customer_name',
            name: 'Cliente',
            invisible: false,
        },
        {
            path: 'delivery_date',
            name: 'Data de Entrega',
            invisible: false,
            render: (row) => {
                const formatted = new Date(row.delivery_date).toLocaleString('pt-BR', {
                    timeZone: 'UTC',
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
                if (formatted === '31/12/1, 23:59' || formatted === '31/12/0001, 23:59') {
                    return '--';
                }
                return formatted;
            }
        },
        {
            path: 'status',
            name: 'Status',
            invisible: false,
            render: (row) => <StatusChip domain="orderService" value={row.status} />,
        },
    ]

    const actions = [
        {
            icon: <Eye size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => {
                    saveFiltersAndOpenOrder(row.order_service_id);
                },
                tooltip: 'Visualizar',
                cursor: 'pointer',
                sx: { mr: 1 }
            }
        },
        {
            icon: (row) => printingOrderId === row.order_service_id ?
                <CircularProgress size={16} color="inherit" /> :
                <Download size={16} strokeWidth={2} />,
            props: {
                onClick: async (row) => {
                    setPrintingOrderId(row.order_service_id);
                    try {
                        await downloadServiceOrderPDF(row.order_service_id, false);
                    } catch (error) {
                        console.error('Erro ao gerar PDF:', error);
                        setMessage('Erro ao gerar PDF da ordem de serviço');
                        setOpenSnackbar(true);
                        setSeverity('error');
                    } finally {
                        setPrintingOrderId(null);
                    }
                },
                tooltip: 'Baixar PDF',
                cursor: 'pointer',
                sx: { mr: 1 },
            }
        },
        ...(canSendWhatsapp ? [{
            icon: <Send size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => {
                    if (!whatsappConnected) {
                        setMessage('WhatsApp não conectado. Conecte o número em Configurações > WhatsApp.');
                        setSeverity('warning');
                        setOpenSnackbar(true);
                        return;
                    }
                    setOrderToWhatsapp(row);
                },
                tooltip: whatsappConnected ? 'Enviar por WhatsApp' : 'WhatsApp não conectado. Conecte em Configurações > WhatsApp.',
                cursor: 'pointer',
                sx: { mr: 1 },
            },
        }] : []),
        {
            icon: <Play size={16} strokeWidth={2} color={theme.palette.primary.main} />,
            props: {
                onClick: (row) => {
                    navigate(`/serviceorders/${row.id}`);
                },
                tooltip: 'Continuar Ordem',
                style: { cursor: 'pointer' }
            },
            ruleShow: 'Iniciada'
        },
        ...(hasPermission('serviceorder.execute') ? [{
            icon: <Play size={16} strokeWidth={2} color={theme.palette.primary.main} />,
            props: {
                onClick: (event, row) => {
                    handleExecuteOrder(event.order_service_id);
                },
                tooltip: 'Iniciar Execução',
                style: { cursor: 'pointer' }
            },
            ruleShow: 'Não Iniciada'
        }] : []),
        ...(hasPermission('serviceorder.execute') ? [{
            icon: <CheckCircle2 size={16} strokeWidth={2} color={theme.palette.success.main} />,
            props: {
                onClick: (event, row) => {
                    setOpenFinishOrderDialog(true);
                    setOrderServiceId(event.order_service_id);
                },
                tooltip: 'Finalizar Ordem',
                style: { cursor: 'pointer' }
            },
            ruleShow: 'Em Execução'
        }] : []),
        ...(hasPermission('serviceorder.bill') ? [{
            icon: <CreditCard size={16} strokeWidth={2} color={theme.palette.secondary.main} />,
            props: {
                onClick: (row) => {
                    setSelectedOrderForPayment(row);
                    setOpenPaymentModal(true);
                },
                tooltip: 'Faturar',
                style: { cursor: 'pointer' }
            },
            ruleShow: 'Finalizada',
        }] : []),
        ...(hasPermission('serviceorder.execute') ? [{
            icon: <RotateCcw size={16} strokeWidth={2} color={theme.palette.warning.main} />,
            props: {
                onClick: (event, row) => {
                    setOpenReopenOrderDialog(true);
                    setOrderServiceId(event.order_service_id);
                },
                tooltip: 'Reabrir Ordem',
                style: { cursor: 'pointer' }
            },
            ruleShow: 'Finalizada',
        }] : []),
        ...(hasPermission('serviceorder.execute') ? [{
            icon: <Wrench size={16} strokeWidth={2} color={theme.palette.primary.main} />,
            props: {
                onClick: (row) => {
                    navigate(`/serviceorder/${row.order_service_id}/operator`);
                },
                tooltip: 'Continuar Execução',
                style: { cursor: 'pointer' }
            },
            ruleShow: 'Em Execução',
        }] : []),
        ...(hasPermission('serviceorder.cancel') ? [{
            icon: <Ban size={16} strokeWidth={2} color={theme.palette.error.main} />,
            props: {
                onClick: (event, row) => {
                    setCancelReason('');
                    setCancelReasonError('');
                    setOpenCancelOrderDialog(true);
                    setOrderServiceId(event.order_service_id);
                },
                tooltip: 'Cancelar Ordem',
                style: { cursor: 'pointer' }
            },
            ruleShow: ['Não Iniciada', 'Em Execução']
        }] : []),
        ...(hasPermission('serviceorder.delete') ? [{
            icon: <Trash2 size={16} strokeWidth={2} color={theme.palette.error.main} />,
            props: {
                onClick: (event, row) => {
                    setOpenDeleteOrderDialog(true);
                    setOrderServiceId(event.order_service_id);
                },
                tooltip: 'Excluir Ordem',
                style: { cursor: 'pointer' }
            },
            // Excluível: cancelada ou não faturada (qualquer status exceto Paga).
            ruleShow: ['Não Iniciada', 'Em Execução', 'Finalizada', 'Cancelada']
        }] : []),
    ]

    // Rótulo do chip fixo (`tb-field` — DESIGN-SYSTEM.md §5.5/§9: micro-label
    // uppercase renderizado fora do campo, não o `InputLabel`/floating label
    // padrão do MUI usado nos formulários de inserção). Mesmo padrão de
    // `sales/List.js`.
    const TbLabel = ({ children }) => (
        <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
            {children}
        </Typography>
    );

    const TB_FIELD_RADIUS = '9px';

    const selectedCustomerName = customers.find(c => String(c.ID) === String(selectedCustomer))?.Name;

    // Shell mobile: lista em cartões do protótipo `ordens/lista.html` (mesmos dados, filtros e navegação).
    if (isMobileShell) {
        return (
            <PageScaffold
                links={[]}
                allowBleed
                titlePageTarget={'Ordens de Serviço'}
                setOpenSnackbar={setOpenSnackbar}
                setMessage={setMessage}
                openSnackbar={openSnackbar}
                severity={severity}
                message={message}
            >
                <MobileOrdersList
                    orders={orders}
                    loading={loading}
                    totalCount={totalCount}
                    hasMore={orders.length < totalCount}
                    onLoadMore={() => setMobilePage((current) => current + 1)}
                    status={selectedStatus}
                    onStatusChange={handleChangeStatus}
                    search={search}
                    onSearchChange={handleTableSearch}
                    customers={customers}
                    customer={selectedCustomer}
                    onCustomerChange={handleChangeCustomer}
                    appliedPeriod={appliedPeriod}
                    onApplyFilters={handleApplyMobileFilters}
                    onClearFilters={handleClearFilters}
                    canCreate={hasPermission('serviceorder.create')}
                    onNew={() => navigate('/serviceorder/generate')}
                    canUpcoming={Boolean(enterpriseSettings?.os_use_vehicle) && hasPermission('serviceorder.upcoming')}
                    onUpcoming={() => navigate('/serviceorders/upcoming')}
                    onOpen={(order) => saveFiltersAndOpenOrder(order.order_service_id)}
                />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold links={[]} titlePageTarget={'Ordens de Serviço'} setOpenSnackbar={setOpenSnackbar} setMessage={setMessage} openSnackbar={openSnackbar} severity={severity} message={message}>
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* Page head — ícone + título + descrição + ação primária,
                    igual a `prototipo-ordem-servico.html` (`page-head`/`page-icon`). */}
                <Box sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            background: (t) => t.palette.gradient.brand,
                        }}>
                            <Wrench size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Ordens de Serviço
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Acompanhe, execute e finalize as ordens de serviço.
                            </Typography>
                        </Box>
                    </Box>

                    {hasPermission('serviceorder.create') && (
                        <NewItemButton label="Nova OS" onClick={() => navigate('/serviceorder/generate')} />
                    )}
                </Box>

                {/* Toolbar de filtros — Status/Cliente/Início/Fim (§6.1
                    `report-toolbar`), igual à tela `view-lista` do protótipo. */}
                <FilterToolbar
                    plain
                    activeCount={(selectedStatus ? 1 : 0) + (selectedCustomer ? 1 : 0) + (selectedDate || selectedDateEnd ? 1 : 0)}
                    onClear={handleClearFilters}
                    onApply={handleApplyFilters}
                    primary={(
                        <Box sx={{ flex: '0 0 220px' }}>
                            <TbLabel>Buscar</TbLabel>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Número, cliente, modelo ou placa…"
                                value={search}
                                onChange={(e) => handleTableSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search size={15} strokeWidth={2} />
                                        </InputAdornment>
                                    ),
                                    sx: { height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS },
                                }}
                            />
                        </Box>
                    )}
                    actions={(
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            {hasActiveFilters && (
                                <Button
                                    variant="text"
                                    onClick={handleClearFilters}
                                    sx={{ height: 40, px: 1.25, color: 'text.secondary', fontSize: '12.5px', fontWeight: 600, '&:hover': { color: 'primary.main', bgcolor: 'transparent', textDecoration: 'underline' } }}
                                >
                                    Limpar
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                startIcon={<Search size={15} strokeWidth={2} />}
                                onClick={handleApplyFilters}
                                sx={{ height: 40, px: 2.25 }}
                            >
                                Buscar
                            </Button>
                        </Stack>
                    )}
                >
                        <Box sx={{ minWidth: 170 }}>
                            <TbLabel>Status</TbLabel>
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={selectedStatus}
                                    displayEmpty
                                    onChange={(e) => handleChangeStatus(e.target.value)}
                                    renderValue={(value) => orderServiceStatusLabel(value) || 'Todos'}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {STATUS_OPTIONS.map((statusOption) => (
                                        <MenuItem key={statusOption} value={statusOption}>
                                            {/* <StatusChip domain="orderService" value={statusOption} sx={{ mr: 1 }} /> */}
                                            {orderServiceStatusLabel(statusOption)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ minWidth: 190 }} data-wide>
                            <TbLabel>Cliente</TbLabel>
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={selectedCustomer}
                                    displayEmpty
                                    onChange={(e) => handleChangeCustomer(e.target.value)}
                                    renderValue={() => selectedCustomerName || 'Todos'}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {customers.map((customer) => (
                                        <MenuItem key={customer.ID} value={customer.ID}>
                                            {customer.Name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box>
                            <TbLabel>Período</TbLabel>
                            <DesktopPeriodSelector
                                start={selectedDate}
                                end={selectedDateEnd}
                                onChange={(start, end) => {
                                    setSelectedDate(start);
                                    setSelectedDateEnd(end);
                                }}
                                showRefresh={false}
                                showShortcuts={false}
                                dualMonth
                                align="start"
                            />
                        </Box>

                </FilterToolbar>

                {/* Chips de filtro ativo + contagem — mesma leitura do
                    `chips-row` do protótipo/`sales/List.js`. */}
                <FilterChips>
                    <Chip
                        label={appliedPeriod.start || appliedPeriod.end
                            ? `Período: ${appliedPeriod.start ? appliedPeriod.start.format('DD/MM/YYYY') : '…'} – ${appliedPeriod.end ? appliedPeriod.end.format('DD/MM/YYYY') : '…'}`
                            : 'Período: todas as datas'}
                        size="small"
                        {...(appliedPeriod.start || appliedPeriod.end ? {
                            onDelete: handleClearPeriod,
                            deleteIcon: <X size={12} strokeWidth={2.5} />,
                        } : {})}
                        sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                    />
                    {selectedStatus && (
                        <Chip
                            label={`Status: ${orderServiceStatusLabel(selectedStatus)}`}
                            size="small"
                            onDelete={() => handleChangeStatus('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    {selectedCustomer && (
                        <Chip
                            label={`Cliente: ${selectedCustomerName || 'N/A'}`}
                            size="small"
                            onDelete={() => handleChangeCustomer('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    {search && (
                        <Chip
                            label={`Busca: "${search}"`}
                            size="small"
                            onDelete={() => handleTableSearch('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    <Chip
                        label={`${totalCount} ordem${totalCount === 1 ? '' : 's'} encontrada${totalCount === 1 ? '' : 's'}`}
                        size="small"
                        sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                    />
                </FilterChips>

                <DefaultTable
                    colunms={columns}
                    data={orders}
                    iconsActions={actions}
                    loading={loading}
                    onRowClick={(row) => saveFiltersAndOpenOrder(row.order_service_id)}
                    rowPerPage={pageSize}
                    rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                    itemLabel="ordens"
                    serverPagination={{
                        page,
                        rowsPerPage: pageSize,
                        total: totalCount,
                        onPageChange: setPage,
                        onRowsPerPageChange: handlePageSizeChange,
                    }}
                />
            </Box>
            <OrderWhatsappDialog
                open={Boolean(orderToWhatsapp)}
                orderId={orderToWhatsapp?.order_service_id}
                orderNumber={orderToWhatsapp?.number}
                onClose={() => setOrderToWhatsapp(null)}
                onResult={(msg, sev) => {
                    setMessage(msg);
                    setSeverity(sev);
                    setOpenSnackbar(true);
                }}
            />
            <DialogForm
                iconType={'warning'}
                open={openExecuteOrderDialog}
                title={'Atenção'}
                confirmButtonText={'Sim, Iniciar'}
                content={'Tem certeza que deseja iniciar a execução da ordem de serviço?'}
                secondaryContent={'Esta ação não pode ser desfeita. A ordem de serviço será iniciada.'}
                onSubmit={() => {
                    httpClient.post(`/orderservices/${orderServiceId}/execute`).then(response => {
                        if (response.status === 200) {
                            navigate(`/serviceorder/${orderServiceId}/operator`);
                        }
                    });
                }}
                onClose={() => {
                    setOpenExecuteOrderDialog(false);
                }}
            />
            <DialogForm
                iconType={'warning'}
                open={openFinishOrderDialog}
                title={'Atenção'}
                confirmButtonText={'Sim, Finalizar'}
                content={'Tem certeza que deseja finalizar a ordem de serviço?'}
                onClose={() => {
                    setOpenFinishOrderDialog(false);
                }}
                onSubmit={async () => {
                    try {
                        const response = await httpClient.post(`/orderservices/${orderServiceId}/finish`);
                        if (response.status === 200) {
                            reloadCurrentPage();
                            setOpenFinishOrderDialog(false);

                        } else {
                            setOpenFinishOrderDialog(false);
                            setOpenErrorOrderDialog(true);
                            setErrorMessage(response.data.error);

                        }
                    } catch (error) {
                        setOpenErrorOrderDialog(true);
                        setErrorMessage(error.response.data.error);
                    }
                }}
            />
            <DialogForm
                iconType={'warning'}
                open={openReopenOrderDialog}
                title={'Atenção'}
                confirmButtonText={'Sim, Reabrir'}
                content={'Tem certeza que deseja reabrir a ordem de serviço?'}
                secondaryContent={'Ela voltará para o status Em Execução.'}
                onClose={() => {
                    setOpenReopenOrderDialog(false);
                }}
                onSubmit={async () => {
                    try {
                        await httpClient.post(`/orderservices/${orderServiceId}/reopen`);
                        setOpenReopenOrderDialog(false);
                        navigate(`/serviceorder/${orderServiceId}/operator`);
                    } catch (error) {
                        setOpenReopenOrderDialog(false);
                        setOpenErrorOrderDialog(true);
                        setErrorMessage(messageFromApiErrorBody(error.response?.data) || 'Erro ao reabrir ordem de serviço');
                    }
                }}
            />
            <DialogForm
                iconType={'warning'}
                open={openCancelOrderDialog}
                title={'Atenção'}
                confirmButtonText={'Sim, Cancelar'}
                content={'Tem certeza que deseja cancelar a ordem de serviço?'}
                secondaryContent={'Esta ação não pode ser desfeita. A ordem de serviço será cancelada permanentemente.'}
                onClose={() => {
                    setOpenCancelOrderDialog(false);
                }}
                onSubmit={() => {
                    const reason = cancelReason.trim();
                    if (!reason) {
                        setCancelReasonError('Informe o motivo do cancelamento.');
                        return;
                    }
                    setCancelReasonError('');
                    setLoading(true);
                    httpClient.put(`/orderservices/cancel/${orderServiceId}`, { reason }).then(response => {
                        if (response.status === 200) {
                            reloadCurrentPage();
                            setOpenCancelOrderDialog(false);
                            setOpenSnackbar(false);
                            setLoading(false);
                            setSeverity('success');
                            setMessage('Ordem de serviço cancelada com sucesso');
                            setOpenSnackbar(true);

                        } else {
                            // validateStatus do httpClient aceita 4xx/5xx: o erro vem em response.data.
                            setLoading(false);
                            setCancelReasonError(
                                (typeof response.data === 'string' && response.data.trim())
                                || messageFromApiErrorBody(response.data)
                                || 'Erro ao cancelar ordem de serviço'
                            );
                        }
                    }).catch(() => {
                        setLoading(false);
                        setCancelReasonError('Erro ao cancelar ordem de serviço');
                    });
                }}
                loading={loading}
                loadingText="Cancelando..."
            >
                <TextField
                    sx={standaloneFieldSx}
                    label="Motivo"
                    fullWidth
                    multiline
                    minRows={3}
                    required
                    autoFocus
                    value={cancelReason}
                    onChange={(e) => {
                        setCancelReason(e.target.value);
                        if (cancelReasonError) setCancelReasonError('');
                    }}
                    error={Boolean(cancelReasonError)}
                    helperText={cancelReasonError}
                    disabled={loading}
                />
            </DialogForm>
            <DialogForm
                iconType={'warning'}
                open={openDeleteOrderDialog}
                title={'Atenção'}
                confirmButtonText={'Sim, Excluir'}
                content={'Tem certeza que deseja excluir a ordem de serviço?'}
                secondaryContent={'Esta ação não pode ser desfeita. A ordem de serviço será excluída permanentemente.'}
                onClose={() => {
                    setOpenDeleteOrderDialog(false);
                }}
                onSubmit={() => {
                    setLoading(true);
                    httpClient.delete(`/orderservices/${orderServiceId}`).then(response => {
                        setLoading(false);
                        setOpenDeleteOrderDialog(false);
                        setOpenSnackbar(false);
                        if (response.status === 200) {
                            reloadCurrentPage();
                            setSeverity('success');
                            setMessage('Ordem de serviço excluída com sucesso');
                        } else {
                            // validateStatus do httpClient aceita 4xx/5xx: o erro vem em response.data.
                            setSeverity('error');
                            setMessage(
                                (typeof response.data === 'string' && response.data.trim())
                                || messageFromApiErrorBody(response.data)
                                || 'Erro ao excluir ordem de serviço'
                            );
                        }
                        setOpenSnackbar(true);
                    }).catch(() => {
                        setLoading(false);
                        setOpenDeleteOrderDialog(false);
                        setSeverity('error');
                        setMessage('Erro ao excluir ordem de serviço');
                        setOpenSnackbar(true);
                    });
                }}
                loading={loading}
                loadingText="Excluindo..."
            />
            {selectedOrderForPayment && (
                <PaymentModal
                    open={openPaymentModal}
                    onClose={() => {
                        setOpenPaymentModal(false);
                        setSelectedOrderForPayment(null);
                    }}
                    total={selectedOrderForPayment.amount || 0}
                    entityId={selectedOrderForPayment.order_service_id}
                    entityType="service_order"
                    onFinish={() => {
                        reloadCurrentPage();
                        setMessage('Pagamento processado com sucesso!');
                        setOpenSnackbar(true);
                        setOpenPaymentModal(false);
                        setSelectedOrderForPayment(null);
                    }}
                />
            )}

            <DialogForm
                iconType={'error'}
                open={openErrorOrderDialog}
                title={'Erro'}
                confirmButtonText={'Entendi!'}
                content={errorMessage}
                onClose={() => {
                    setOpenErrorOrderDialog(false);
                }}
            />
        </PageScaffold>
    )
}
